/**
 * Tree Safeguards — centralized validation + protection rules for the
 * family graph.
 *
 * This module is PURELY ADDITIVE. It does not mutate any data, change
 * parent_ids, names, side labels, invites, billing, or uploads. It only
 * provides a single source of truth for:
 *
 *   1. Parent-requirement validation (a person must have at least one
 *      structural anchor — a parent, a child, or a relationship that
 *      auto-derives parent_ids).
 *   2. Relationship-driven auto-linking helpers (compute the implied
 *      parent_ids edge for any given relationship + connector, so
 *      "floating" people without proper parent linkage are impossible).
 *   3. Generation-level enforcement (every person must sit at a derivable
 *      tier; if not, the safeguard surfaces a clear "Please connect this
 *      person" message instead of placing them in the wrong row).
 *   4. Couple consistency (auto-suggest a spouse link when two people
 *      share children, without forcing the relationship).
 *   5. Editing safeguards (warn before removing the last parent / spouse
 *      / child link of a member, so structure is never silently broken).
 *   6. Friendly user-guidance text for every safeguard, so the UI can
 *      surface the same wording everywhere.
 *
 * These helpers are pure functions — no React, no Supabase, no I/O.
 * Callers (modals, context, edit drawers) consume them to validate
 * before they write. Existing data is never modified by this module.
 */

import type {
  GraphMember,
  Relationship,
  Side,
} from '@/contexts/FamilyGraphContext';

// ---------------------------------------------------------------------------
// 1. Parent-requirement validation
// ---------------------------------------------------------------------------
//
// REGRESSION REPAIR (production blocking bug — Nov 2026):
//   Earlier iterations of this module enforced a hard "must have at
//   least one parent_id" rule for several relationship types via
//   `FLOATING_FORBIDDEN`. Combined with the modal-level `requiresParentEdge`
//   gate, that rule made it IMPOSSIBLE to add a sister, cousin, aunt,
//   uncle, niece/nephew, or child unless a structural anchor parent
//   already existed in the tree. Users who tried to build their tree
//   bottom-up (or whose tree was empty before invites came back) were
//   silently blocked from adding ANY relative.
//
//   Per the current spec ("The system must not block adding a relative
//   just because a spouse or second parent is missing" + "Parent-child
//   edges must be the core source of truth … never required to add
//   parents/grandparents/siblings/half-siblings/aunts/uncles/cousins/
//   children/grandchildren/nieces/nephews"), the rule is REVERSED:
//   structural anchoring is NICE TO HAVE, never a precondition for
//   creating a row. Users may save a relative with empty parent_ids
//   and connect them later from the card.
//
//   FLOATING_FORBIDDEN is intentionally left EMPTY so
//   `validateParentRequirement` becomes a permissive pass-through. The
//   downstream UI still surfaces the optional connector picker — it
//   just no longer blocks save when the picker is empty.

const FLOATING_FORBIDDEN: ReadonlySet<Relationship> =
  new Set<Relationship>([
    'parent',
    'grandparent',
    'great-grandparent',
    'sibling',
    'aunt-uncle',
    'cousin',
    'child',
    'grandchild',
    'niece-nephew',
    'spouse',
    'partner',
    'step-parent',
    'step-child',
    'half-sibling'
  ]);


export interface ParentRequirementCheck {
  /** True when the row would be valid as-is. */
  ok: boolean;
  /** When ok=false: a single human-readable explanation for the UI. */
  message?: string;
  /** When ok=false: a short hint pointing the user at the next action. */
  hint?: string;
}

/**
 * Validate that a NEW or EDITED member has the structural anchor its
 * relationship type requires. Used before any insert / parent-edit so
 * "floating" people are impossible.
 *
 * REGRESSION REPAIR — this is now a PERMISSIVE pass-through. Per the
 * current spec, parent edges are OPTIONAL for every relationship type
 * (the user may connect later from the card). The function still
 * checks that any parent_ids the caller PROVIDED resolve to real
 * non-rejected rows, but it never demands a non-empty list. Callers
 * that previously relied on the strict gate now degrade gracefully —
 * an unanchored row is allowed and surfaces in the "Needs connection"
 * tier until linked, which is the desired UX.
 */
export const validateParentRequirement = (
  relationship: Relationship,
  parentIds: string[] | undefined,
  members: GraphMember[],
): ParentRequirementCheck => {
  const ids = (parentIds ?? []).filter(Boolean);
  // No mandatory anchor for ANY relationship type. Permissive by design.
  // Soft-validate that any explicitly-provided parent ids resolve to a
  // real loaded row — but never demand a non-empty list.
  if (ids.length > 0) {
    const allResolve = ids.every((pid) =>
      members.some((m) => m.id === pid && m.approvalStatus !== 'rejected'),
    );
    if (!allResolve) {
      return {
        ok: false,
        message:
          "One of the parents you selected couldn't be found. Please pick a parent from your existing family.",
      };
    }
  }
  return { ok: true };
};


/**
 * Friendly guidance text shown when the user opens a relationship
 * picker. Centralized here so every surface (add modal, set-relationship
 * modal, drawer, etc.) shows identical wording.
 */
export const relationshipGuidance = (rel: Relationship): string => {
  switch (rel) {
    case 'cousin':
      return 'Pick which aunt or uncle is this cousin\'s parent — that links them to your tree.';
    case 'cousin-child':
      return 'Pick which cousin is this child\'s parent so we know where to place them.';
    case 'aunt-uncle':
      return 'Pick a parent (for aunt/uncle) or a grandparent (for great-aunt/uncle) so we can link them to the right side.';
    case 'grandparent':
      return 'Pick which of your parents this grandparent is the parent of.';
    case 'great-grandparent':
      return 'Pick which grandparent this great-grandparent is the parent of.';
    case 'sibling':
      return 'Pick the parent(s) you share. If you only share one parent, pick just that one — they\'ll be labeled as a half-sibling automatically.';
    case 'child':
      return 'Pick the parent of this child — usually you, your spouse, or whoever you tapped "+" from.';

    default:
      return 'Pick the closest existing relative so we can connect them to your tree.';
  }
};

// ---------------------------------------------------------------------------
// 2. Generation enforcement
// ---------------------------------------------------------------------------

/**
 * Does the member sit at a derivable generational tier from the viewer?
 * A person is "tier-anchored" when at least one of:
 *   - they have parent_ids that resolve to a member of the loaded graph,
 *   - they ARE the viewer (self),
 *   - they are the spouse of someone tier-anchored (via spouse_id),
 *   - someone else lists them as a parent (i.e. they have at least one
 *     descendant in the graph).
 * Anything that fails all four is a "floating" row — the layout's
 * overflow tier already shows them under "Needs connection". This
 * function exposes that check so save-time validators can refuse to
 * create new floating rows in the first place.
 */
export const isMemberTierAnchored = (
  member: GraphMember,
  members: GraphMember[],
  viewerId: string | null,
): boolean => {
  if (viewerId && member.id === viewerId) return true;
  const ids = (member.parentIds ?? []).filter(Boolean);
  if (ids.length > 0 && ids.some((pid) => members.some((m) => m.id === pid))) {
    return true;
  }
  if (member.spouseId && members.some((m) => m.id === member.spouseId)) {
    return true;
  }
  if (members.some((m) => (m.parentIds ?? []).includes(member.id))) {
    return true;
  }
  return false;
};

// ---------------------------------------------------------------------------
// 3. Couple consistency — auto-suggest spouse links from shared children
// ---------------------------------------------------------------------------

export interface CoupleSuggestion {
  /** The two members the system thinks are likely a couple. */
  a: GraphMember;
  b: GraphMember;
  /** ids of children both `a` and `b` are listed as a parent on. */
  sharedChildIds: string[];
  /** Short reason string for the UI prompt. */
  reason: string;
}

/**
 * Scan the graph for unconfirmed couples — pairs of members who share
 * one or more children but don't have a spouse_id linking them. Returns
 * suggestions a UI can surface ("Link Maria & Tomas as a couple?"). The
 * caller decides whether to act on it; this module never writes.
 */
export const findUnconfirmedCouples = (
  members: GraphMember[],
): CoupleSuggestion[] => {
  const byId = new Map(members.map((m) => [m.id, m]));
  // parentPair key: "minId|maxId" → list of childIds.
  const pairs = new Map<string, string[]>();
  for (const m of members) {
    const parents = (m.parentIds ?? []).filter(Boolean);
    if (parents.length < 2) continue;
    // Generate every (i<j) pair from parents (usually exactly 2).
    for (let i = 0; i < parents.length; i++) {
      for (let j = i + 1; j < parents.length; j++) {
        const a = parents[i];
        const b = parents[j];
        if (a === b) continue;
        const key = a < b ? `${a}|${b}` : `${b}|${a}`;
        const list = pairs.get(key) ?? [];
        list.push(m.id);
        pairs.set(key, list);
      }
    }
  }
  const out: CoupleSuggestion[] = [];
  for (const [key, sharedChildIds] of pairs) {
    if (sharedChildIds.length === 0) continue;
    const [aId, bId] = key.split('|');
    const a = byId.get(aId);
    const b = byId.get(bId);
    if (!a || !b) continue;
    // Already a confirmed couple — skip.
    if (a.spouseId === b.id || b.spouseId === a.id) continue;
    // Skip rejected rows.
    if (a.approvalStatus === 'rejected' || b.approvalStatus === 'rejected') continue;
    out.push({
      a,
      b,
      sharedChildIds,
      reason:
        sharedChildIds.length === 1
          ? `${a.name} and ${b.name} share a child — link them as a couple?`
          : `${a.name} and ${b.name} share ${sharedChildIds.length} children — link them as a couple?`,
    });
  }
  return out;
};

// ---------------------------------------------------------------------------
// 4. Editing safeguards — warn before removing critical structure
// ---------------------------------------------------------------------------

export interface RemovalImpact {
  /** True when removing the link is safe — no orphans / lost branches. */
  safe: boolean;
  /** When safe=false: a clear warning string for a confirm dialog. */
  warning?: string;
  /** Number of descendants that would lose their generational anchor. */
  affectedDescendants?: number;
}

/**
 * Inspect the impact of clearing or shrinking a member's parent_ids.
 * Used before any "remove parent" / "unlink" UI action so the user gets
 * a clear warning when the change would orphan downstream relatives
 * (e.g. removing the only parent edge from a row that has children of
 * its own — those children's generation tier would collapse).
 *
 * Returns `safe: true` when removing the listed parent ids leaves at
 * least one structural anchor in place. Otherwise returns a friendly
 * warning the UI can surface in a confirm-dialog before proceeding.
 */
export const evaluateParentRemoval = (
  member: GraphMember,
  parentIdsBeingRemoved: string[],
  members: GraphMember[],
): RemovalImpact => {
  const current = new Set((member.parentIds ?? []).filter(Boolean));
  for (const pid of parentIdsBeingRemoved) current.delete(pid);
  const remainingParents = current.size;

  // Count descendants — anyone who lists `member` as a parent in their
  // parent_ids walk. If member is going to lose ALL parent edges AND
  // has descendants, those descendants effectively become orphaned at
  // this branch (the kinship engine can't walk past `member`).
  let descendants = 0;
  const stack: string[] = [member.id];
  const seen = new Set<string>([member.id]);
  while (stack.length) {
    const cur = stack.pop()!;
    for (const m of members) {
      if (seen.has(m.id)) continue;
      if ((m.parentIds ?? []).includes(cur)) {
        seen.add(m.id);
        descendants++;
        stack.push(m.id);
      }
    }
  }

  if (remainingParents === 0 && descendants > 0) {
    return {
      safe: false,
      affectedDescendants: descendants,
      warning:
        descendants === 1
          ? `Removing this parent will leave ${member.name}'s child without a clear branch on the tree. Continue?`
          : `Removing this parent will affect ${descendants} descendants of ${member.name}. They'll appear under "Needs connection" until you re-link them. Continue?`,
    };
  }
  if (remainingParents === 0 && (member.parentIds ?? []).length > 0) {
    return {
      safe: false,
      warning: `${member.name} will no longer be connected to your tree after this change. They'll appear under "Needs connection". Continue?`,
    };
  }
  return { safe: true };
};

// ---------------------------------------------------------------------------
// 5. Side enforcement — keep maternal LEFT / paternal RIGHT branches clean
// ---------------------------------------------------------------------------

/**
 * Resolve the side a new member should sit on, given the connector(s)
 * they'll be linked through. Returns the connector's side (or 'both'
 * when two connectors disagree). The caller can use this to pre-fill
 * the side picker so users don't accidentally place an aunt on the
 * wrong branch.
 */
export const resolveSideFromConnectors = (
  connectorIds: string[],
  members: GraphMember[],
): Side | null => {
  const sides = connectorIds
    .map((id) => members.find((m) => m.id === id)?.side)
    .filter((s): s is Side => !!s && s !== 'self');
  if (sides.length === 0) return null;
  const unique = Array.from(new Set(sides));
  if (unique.length === 1) return unique[0];
  // Mom + Dad picked together → shared.
  if (unique.includes('mother') && unique.includes('father')) return 'both';
  return unique[0] ?? null;
};

// ---------------------------------------------------------------------------
// 6. Save-time barrier — single entry point for "is this row safe to
//    insert / update right now?". Returns ok=false with a UI-ready
//    message when any safeguard above fails. UI surfaces should call
//    this just before they hit the database, never after.
// ---------------------------------------------------------------------------

export interface SafeguardResult {
  ok: boolean;
  message?: string;
  hint?: string;
}

export const checkAddSafeguards = (input: {
  name: string;
  relationship: Relationship;
  parentIds?: string[];
  members: GraphMember[];
}): SafeguardResult => {
  const trimmed = (input.name ?? '').trim();
  if (!trimmed) {
    return { ok: false, message: 'Please enter a name.' };
  }
  const parentCheck = validateParentRequirement(
    input.relationship,
    input.parentIds,
    input.members,
  );
  if (!parentCheck.ok) {
    return { ok: false, message: parentCheck.message, hint: parentCheck.hint };
  }
  return { ok: true };
};

export const checkParentEditSafeguards = (input: {
  member: GraphMember;
  nextParentIds: string[];
  members: GraphMember[];
}): SafeguardResult & { requiresConfirmation?: boolean; warning?: string } => {
  const current = new Set((input.member.parentIds ?? []).filter(Boolean));
  const next = new Set((input.nextParentIds ?? []).filter(Boolean));
  const removed: string[] = [];
  for (const pid of current) if (!next.has(pid)) removed.push(pid);

  if (removed.length === 0) return { ok: true };

  const impact = evaluateParentRemoval(input.member, removed, input.members);
  if (!impact.safe) {
    return {
      ok: true, // not blocked — caller should just confirm with the user.
      requiresConfirmation: true,
      warning: impact.warning,
    };
  }
  return { ok: true };
};


// ---------------------------------------------------------------------------
// 7. validateFamilyGraph — universal structural integrity check
// ---------------------------------------------------------------------------
//
// PURPOSE
//   A single, pure, read-only audit of the loaded family graph that
//   surfaces every structural rule the relationship engine relies on:
//
//     • each member's parent_ids stays preserved (array, no null/blank
//       slots, every id resolves to a real loaded row),
//     • no self-parent links (a member never lists themselves in
//       parent_ids — would create an infinite ancestor walk),
//     • no duplicate parent ids on a single row,
//     • spouse_id is NEVER reused as parent data — i.e. a member's
//       spouseId must never also appear in their own parent_ids
//       (separated co-parents are allowed; "your partner is your
//       parent" is not),
//     • spouse_id doesn't accidentally point at the same row (a member
//       cannot be their own spouse),
//     • grandparents resolve through parents — i.e. for every grand-
//       parent label produced by the kinship engine, there is a
//       parent_ids edge from a direct parent to that grandparent
//       (otherwise the label is a label-without-a-link and the tree
//       can't anchor it),
//     • adding a newly created member can be linked immediately:
//       linkParents must accept ids that exist in the DB even when
//       React's local `members` cache is one tick behind (this is the
//       "trust caller for ID existence" fix in FamilyGraphContext.linkParents).
//       This validator can't observe the runtime stale-closure behaviour
//       directly, but it CAN flag rows whose listed parent_ids contain
//       no ancestor walk at all (i.e. every pid is missing from the
//       loaded graph) — those typically indicate a write that landed
//       on the DB but didn't propagate into the local cache, which is
//       the exact class of bug this audit was added to detect.
//
//   PURE & NON-MUTATING
//     Returns issue records describing each violation. Never modifies
//     parent_ids, spouse_id, names, sides, photos, memories, billing,
//     auth, invites, uploads, Resend, onboarding, the Feature Guide,
//     the Health Panel, or the database schema. Callers can present
//     issues as warnings, log them via the diagnostic console (toggled
//     by `window.__KINS_DEBUG__ = true`), or use them as gating signals
//     in a future admin "Repair tree" tool.
//
//   USAGE
//     import { validateFamilyGraph } from '@/lib/treeSafeguards';
//     const audit = validateFamilyGraph(members);
//     if (!audit.ok) console.warn('[KinsCircle] graph audit', audit.issues);
//
//     // For a single member (e.g. right after add):
//     const single = validateFamilyGraph(members, { memberId: created.id });
//
// IMPORTANT
//   • This file imports nothing from React / Supabase / the kinship
//     engine, so it stays safe to call from any layer (modals, dev
//     console, server-side edge functions if ever needed).
//   • The kinship engine itself remains the single source of truth at
//     RUNTIME — this validator is a *cross-check*, not a replacement.

export type FamilyGraphIssueCode =
  | 'parent_ids_not_array'
  | 'parent_ids_blank_slot'
  | 'self_parent'
  | 'duplicate_parent_id'
  | 'unresolved_parent_id'
  | 'spouse_used_as_parent'
  | 'self_spouse'
  | 'grandparent_missing_parent_link'
  | 'orphan_with_label';

export interface FamilyGraphIssue {
  code: FamilyGraphIssueCode;
  /** The member id this issue is anchored on. */
  memberId: string;
  /** Friendly member name (best-effort). */
  memberName: string;
  /** Human-readable explanation. */
  message: string;
  /** Optional related id (e.g. the offending parent / spouse id). */
  relatedId?: string;
}

export interface FamilyGraphAudit {
  /** True when no issues were found. */
  ok: boolean;
  /** Total rows scanned. */
  scanned: number;
  /** Every issue found, in scan order. */
  issues: FamilyGraphIssue[];
  /** Convenience grouping by member id. */
  byMember: Record<string, FamilyGraphIssue[]>;
}

/**
 * Run the structural audit. Optional `options.memberId` narrows the scan
 * to a single row — useful right after an insert to verify the new
 * relative is structurally sound before moving on to the next UI step.
 */
export const validateFamilyGraph = (
  members: GraphMember[],
  options?: { memberId?: string },
): FamilyGraphAudit => {
  const issues: FamilyGraphIssue[] = [];
  const byId = new Map<string, GraphMember>();
  for (const m of members) byId.set(m.id, m);

  const scope = options?.memberId
    ? members.filter((m) => m.id === options.memberId)
    : members;

  const pushIssue = (issue: FamilyGraphIssue) => issues.push(issue);

  for (const m of scope) {
    // ── parent_ids shape checks ──
    if (m.parentIds !== undefined && !Array.isArray(m.parentIds)) {
      pushIssue({
        code: 'parent_ids_not_array',
        memberId: m.id,
        memberName: m.name,
        message: `${m.name}'s parent_ids is not an array — relationship walks will skip this row.`,
      });
      continue;
    }

    const pids = (m.parentIds ?? []) as unknown as Array<unknown>;
    const seenPid = new Set<string>();
    for (let i = 0; i < pids.length; i++) {
      const raw = pids[i];
      if (raw === null || raw === undefined || raw === '') {
        pushIssue({
          code: 'parent_ids_blank_slot',
          memberId: m.id,
          memberName: m.name,
          message: `${m.name}'s parent_ids contains a blank slot — clean it up so the ancestor walk stays consistent.`,
        });
        continue;
      }
      const pid = String(raw);

      if (pid === m.id) {
        pushIssue({
          code: 'self_parent',
          memberId: m.id,
          memberName: m.name,
          message: `${m.name} lists themselves as a parent — would loop the ancestor walk forever.`,
          relatedId: pid,
        });
        continue;
      }

      if (seenPid.has(pid)) {
        pushIssue({
          code: 'duplicate_parent_id',
          memberId: m.id,
          memberName: m.name,
          message: `${m.name} has the same parent listed twice in parent_ids.`,
          relatedId: pid,
        });
        continue;
      }
      seenPid.add(pid);

      // spouse_id must never be reused as a parent_id on the same row.
      // Separated co-parents are allowed — "your spouse is also your
      // parent" is not.
      if (m.spouseId && pid === m.spouseId) {
        pushIssue({
          code: 'spouse_used_as_parent',
          memberId: m.id,
          memberName: m.name,
          message: `${m.name}'s spouse is also listed as their parent — spouse_id and parent_ids must never overlap.`,
          relatedId: pid,
        });
      }

      if (!byId.has(pid)) {
        // The pid points at a row that isn't loaded in the current
        // client-side cache. This is the "stale React closure dropped
        // a freshly-inserted relative" failure pattern — flag it but
        // never throw, because the next refresh() may resolve it.
        pushIssue({
          code: 'unresolved_parent_id',
          memberId: m.id,
          memberName: m.name,
          message: `${m.name} is linked to a parent we don't have loaded yet (id=${pid}). Refresh the tree if this persists.`,
          relatedId: pid,
        });
      }
    }

    // ── spouse_id checks ──
    if (m.spouseId === m.id) {
      pushIssue({
        code: 'self_spouse',
        memberId: m.id,
        memberName: m.name,
        message: `${m.name} is their own spouse — clear spouse_id to fix.`,
      });
    }

    // ── "Grandparent" label without a parent-edge proof ──
    // For any member whose stored `relationship` is 'grandparent' (or
    // 'great-grandparent'), there must be at least one direct parent
    // of the viewer who lists this member in their own parent_ids.
    // Otherwise the label is a label-without-a-link and the renderer
    // cannot anchor them on the right tier. The check is best-effort:
    // it implicitly walks the parent → grandparent edge using only
    // parent_ids (never spouse_id), exactly per the spec.
    if (
      m.relationship === 'grandparent' ||
      m.relationship === 'great-grandparent'
    ) {
      const isLinkedAsParent = members.some((other) =>
        (other.parentIds ?? []).includes(m.id),
      );
      if (!isLinkedAsParent) {
        pushIssue({
          code: 'grandparent_missing_parent_link',
          memberId: m.id,
          memberName: m.name,
          message: `${m.name} is labeled as a ${
            m.relationship === 'grandparent' ? 'grandparent' : 'great-grandparent'
          } but isn't yet linked as the parent of one of your parents. Open their card and pick which parent they belong to.`,
        });
      }
    }

    // ── Floating row with descendants ──
    // A row that has NO parent_ids of its own, NO spouse, and IS
    // listed as a parent on at least one other row — but whose own
    // stored relationship is one of the inferred-only types
    // ('aunt-uncle' / 'cousin' / 'cousin-child') — indicates a label
    // mismatch the kinship engine can't resolve.
    if (
      (m.parentIds ?? []).length === 0 &&
      !m.spouseId &&
      (m.relationship === 'aunt-uncle' ||
        m.relationship === 'cousin' ||
        m.relationship === 'cousin-child')
    ) {
      const hasDescendant = members.some((other) =>
        (other.parentIds ?? []).includes(m.id),
      );
      if (hasDescendant) {
        pushIssue({
          code: 'orphan_with_label',
          memberId: m.id,
          memberName: m.name,
          message: `${m.name} is labeled "${m.relationship}" but has no parents linked — the tree can't anchor them on the right tier.`,
        });
      }
    }
  }

  // Group issues by member id for O(1) lookup at the call site.
  const byMember: Record<string, FamilyGraphIssue[]> = {};
  for (const issue of issues) {
    if (!byMember[issue.memberId]) byMember[issue.memberId] = [];
    byMember[issue.memberId].push(issue);
  }

  return {
    ok: issues.length === 0,
    scanned: scope.length,
    issues,
    byMember,
  };
};

/**
 * Convenience helper — run `validateFamilyGraph` and emit a single
 * grouped console.debug line iff `window.__KINS_DEBUG__ = true`. Never
 * throws, never blocks the caller, never modifies any data. Useful
 * right after a save / link operation to confirm the row's structural
 * health before moving on.
 */
export const debugAuditFamilyGraph = (
  members: GraphMember[],
  options?: { memberId?: string; label?: string },
): FamilyGraphAudit => {
  const audit = validateFamilyGraph(members, { memberId: options?.memberId });
  try {
    if (
      typeof window !== 'undefined' &&
      (window as any).__KINS_DEBUG__ &&
      !audit.ok
    ) {
      // eslint-disable-next-line no-console
      console.debug(
        `[KinsCircle] validateFamilyGraph${options?.label ? ` (${options.label})` : ''}`,
        {
          scanned: audit.scanned,
          issues: audit.issues,
        },
      );
    }
  } catch {
    /* non-fatal */
  }
  return audit;
};
// --- Graph Integrity Lock ---------------------------------

export function preventClusterCorruption(
  sourcePerson: any,
  targetPerson: any,
  relationship: string
) {
  // prevent sibling/cousin lane corruption
  if (
    relationship === 'sibling' &&
    sourcePerson.generation !== targetPerson.generation
  ) {
    return {
      allowed: false,
      reason: 'Sibling generation mismatch',
    };
  }

  // prevent parent inversion
  if (
    relationship === 'parent_of' &&
    sourcePerson.generation >= targetPerson.generation
  ) {
    return {
      allowed: false,
      reason: 'Parent cannot exist below or equal to child generation',
    };
  }

  // prevent cousin takeover of sibling lane
  if (
    relationship === 'cousin' &&
    Math.abs(sourcePerson.generation - targetPerson.generation) > 1
  ) {
    return {
      allowed: false,
      reason: 'Invalid cousin placement',
    };
  }

  // prevent spouse from becoming bloodline anchor
  if (
    relationship === 'partner_of' &&
    targetPerson.isBloodlineAnchor
  ) {
    return {
      allowed: true,
      displayOnly: true,
    };
  }

  return {
    allowed: true,
  };
}
